import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  showDetails?: boolean;
}

const ErrorContainer = styled.div`
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  border: 1px solid #e9ebed;
  margin: 20px;
  
  @media (max-width: 768px) {
    margin: 10px;
    padding: 30px 15px;
    min-height: 300px;
  }
`;

const ErrorIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #8D4F1B 0%, #703815 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(141, 79, 27, 0.2);
  
  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    margin-bottom: 20px;
    
    svg {
      width: 30px;
      height: 30px;
    }
  }
`;

const ErrorTitle = styled.h2`
  color: #2f3941;
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 12px 0;
  text-align: center;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 8px;
  }
`;

const ErrorMessage = styled.p`
  color: #68737d;
  font-size: 16px;
  line-height: 1.6;
  text-align: center;
  margin: 0 0 32px 0;
  max-width: 500px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const ErrorActions = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    max-width: 280px;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.variant === 'primary' ? `
    background: #8D4F1B;
    color: white;
    
    &:hover {
      background: #703815;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(141, 79, 27, 0.3);
    }
  ` : `
    background: white;
    color: #8D4F1B;
    border: 1px solid #8D4F1B;
    
    &:hover {
      background: #8D4F1B;
      color: white;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(141, 79, 27, 0.2);
    }
  `}
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 14px 20px;
  }
`;

const DetailsToggle = styled.button`
  background: none;
  border: none;
  color: #8D4F1B;
  font-size: 14px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  margin-top: 20px;
  text-decoration: underline;
  
  &:hover {
    color: #703815;
  }
`;

const ErrorDetails = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ebed;
  max-width: 600px;
  width: 100%;
  
  pre {
    font-size: 12px;
    color: #2f3941;
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
    line-height: 1.4;
  }
  
  @media (max-width: 768px) {
    margin-top: 15px;
    padding: 15px;
    
    pre {
      font-size: 11px;
    }
  }
`;

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorContainer>
          <ErrorIcon>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </ErrorIcon>
          
          <ErrorTitle>Oops! Something went wrong</ErrorTitle>
          
          <ErrorMessage>
            We're sorry, but something unexpected happened. This error has been logged and our team will look into it. 
            In the meantime, you can try refreshing the page or going back to the dashboard.
          </ErrorMessage>
          
          <ErrorActions>
            <ActionButton variant="primary" onClick={this.handleReload}>
              Refresh Page
            </ActionButton>
            <ActionButton variant="secondary" onClick={this.handleGoHome}>
              Go to Dashboard
            </ActionButton>
          </ErrorActions>
          
          {process.env.NODE_ENV === 'development' && (
            <>
              <DetailsToggle onClick={this.toggleDetails}>
                {this.state.showDetails ? 'Hide' : 'Show'} Error Details
              </DetailsToggle>
              
              {this.state.showDetails && (
                <ErrorDetails>
                  <pre>
                    {this.state.error?.toString()}
                    {'\n\n'}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </ErrorDetails>
              )}
            </>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
